import { formatEventDate } from "../utils/formatEventDate"
import MasonryCollection from "./components/MasonryCollection"
import { GalleryDesignProps } from "./types"
import PhotographerBar from "./components/PhotographerBar"
import CoverImage from "./components/CoverImage"

/**
 * Occasion · Sports
 * Light + dark. A high-energy cover: a full-bleed photo under a dark wash with a
 * huge condensed Anton headline, a skewed electric-lime accent bar, and a chunky
 * date badge — bottom-left, bold and dynamic. Photos use the shared Masonry
 * collection with a tighter gap. Color mode from the body `dark` class.
 */
const SportsGallery = ({
  data,
  folderBar,
  contentMode,
  aiContent,
  emptyContent,
  photos,
  onCoverLoad,
}: GalleryDesignProps) => {
  const name = data?.name || ""
  const dateLabel = formatEventDate(data?.startDate, data?.endDate, data?.date)
  const cover = data?.coverPhotoLink
  const vendor = data?.vendors?.[0]

  return (
    <div className="relative w-full bg-white text-[#0a0a0a] dark:bg-[#0a0a0a] dark:text-white font-archivo">
      <PhotographerBar vendor={vendor} tone="light" />

      {/* Full-bleed cover with a dark wash and a bold bottom-left lockup. */}
      <div className="relative w-full h-[74vh] md:h-[94vh] bg-[#111] overflow-hidden">
        {cover && (
          <CoverImage
            src={cover}
            alt={name}
            className="w-full h-full object-cover"
            onLoad={onCoverLoad}
          />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/30" />

        <div className="absolute inset-x-0 bottom-0 px-5 md:px-12 pb-9 md:pb-14 text-white">
          <p className="inline-block bg-[#d9ff3d] text-black text-[10px] md:text-[12px] font-bold tracking-[0.28em] uppercase px-3 py-1 -skew-x-6">
            <span className="inline-block skew-x-6">Event Highlights</span>
          </p>
          <h1 className="font-anton uppercase leading-[0.9] tracking-[0.01em] text-[52px] md:text-[104px] lg:text-[124px] mt-3 md:mt-4 break-words drop-shadow-[0_3px_18px_rgba(0,0,0,0.5)]">
            {name}
          </h1>
          {/* Skewed accent bar + date */}
          <div className="flex items-center gap-4 mt-2 md:mt-3">
            <span className="h-[6px] w-16 md:w-28 bg-[#d9ff3d] -skew-x-[20deg]" />
            {dateLabel && (
              <span className="font-anton uppercase tracking-[0.08em] text-base md:text-2xl text-[#d9ff3d]">
                {dateLabel}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center w-full">
        {folderBar}

        <div className="w-full px-3 md:px-10 pt-6 pb-10 min-h-[40vh]">
          {contentMode === "ai" ? (
            aiContent
          ) : contentMode === "photos" ? (
            <MasonryCollection
              photos={photos}
              columnsClass="columns-2 md:columns-3 lg:columns-4"
              gap={6}
            />
          ) : (
            emptyContent
          )}
        </div>
      </div>
    </div>
  )
}

export default SportsGallery
